import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationsApi, KnowlezNotification } from '@/lib/notifications';

export function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: notifs = [] } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => notificationsApi.list(true),
    // Backstop only — the realtime socket pushes new rows immediately
    // via the `notification:new` custom event below. Five-minute poll
    // catches the rare case where a tab missed a push during reconnect.
    refetchInterval: 5 * 60_000,
  });

  // Realtime — invalidate on any push from the gateway.
  useEffect(() => {
    const onNew = () => qc.invalidateQueries({ queryKey: ['notifications'] });
    const onRead = () => qc.invalidateQueries({ queryKey: ['notifications'] });
    window.addEventListener('notification:new', onNew);
    window.addEventListener('notification:read', onRead);
    return () => {
      window.removeEventListener('notification:new', onNew);
      window.removeEventListener('notification:read', onRead);
    };
  }, [qc]);

  const markRead = useMutation({
    mutationFn: (ids: string[]) => notificationsApi.markRead(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.notifications')}
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-ink-100 text-ink-600"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM7 16a3 3 0 006 0H7z" /></svg>
        {notifs.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-warn-500 text-white num text-[10px] leading-4 text-center font-semibold">
            {notifs.length > 9 ? '9+' : notifs.length}
          </span>
        )}
      </button>

      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 mt-2 w-80 rounded-xl border border-ink-200 bg-white shadow-lg z-50"
        >
          <div className="px-4 py-2 border-b border-ink-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-ink-900">{t('nav.notifications')}</span>
            {notifs.length > 0 && (
              <button
                onClick={() => markRead.mutate(notifs.map((n) => n.id))}
                className="text-xs text-accent-600 hover:underline"
              >
                {t('notifications.mark_all_read')}
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {notifs.length === 0 ? (
              <li className="px-4 py-6 text-sm text-ink-500 text-center">{t('notifications.empty')}</li>
            ) : (
              notifs.map((n) => <Item key={n.id} n={n} onRead={(id) => markRead.mutate([id])} t={t} />)
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Item({ n, onRead, t }: { n: KnowlezNotification; onRead: (id: string) => void; t: (k: string) => string }) {
  return (
    <li className="px-4 py-3 border-b border-ink-50 last:border-0 hover:bg-ink-50">
      <div className="text-sm font-medium text-ink-900">{n.title}</div>
      <p className="mt-0.5 text-xs text-ink-600 leading-relaxed">{n.body}</p>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-xs num text-ink-400">{new Date(n.createdAt).toLocaleString()}</span>
        <div className="flex gap-3">
          {n.link && <a href={n.link} className="text-xs text-accent-600 hover:underline">{t('notifications.view')}</a>}
          <button onClick={() => onRead(n.id)} className="text-xs text-ink-500 hover:text-ink-900">{t('notifications.mark_read')}</button>
        </div>
      </div>
    </li>
  );
}
